/**
 * React Query hooks for tenant-defined relationship classes (Phase 3).
 *
 * Classes are tenant-global (shared by companies AND contacts) and feed the same
 * custom-field inheritance resolver as the built-in relationship enum. The admin
 * manager uses the write hooks; org/contact detail pages + the field-definition
 * scope picker use the read hook. See docs/platform/custom-fields-design.md.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createRelationshipClassApiV1MasterDataContactsRelationshipClassesPost,
  relationshipClassCarrierCountsApiV1MasterDataContactsRelationshipClassesCarrierCountsGet,
  deleteRelationshipClassApiV1MasterDataContactsRelationshipClassesClassIdDelete,
  listRelationshipClassesApiV1MasterDataContactsRelationshipClassesGet,
  updateRelationshipClassApiV1MasterDataContactsRelationshipClassesClassIdPut,
} from '../../../generated/api/contacts/contacts';
import type { RelationshipClassCreateRequest } from '../../../generated/api/models/relationshipClassCreateRequest';
import type { RelationshipClassUpdateRequest } from '../../../generated/api/models/relationshipClassUpdateRequest';

/** A tenant-defined relationship class (response shape from the API). */
export interface RelationshipClass {
  id: string;
  tenant_id: string;
  key: string;
  label: string;
  label_translations: Record<string, string>;
  color: string | null;
  icon: string | null;
  position: number;
  is_active: boolean;
  scope_token: string;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Where a tenant admin defines custom relationships.
 *
 * Spelled once, here, because the detail-page pickers link to it from their
 * empty state and a hardcoded internal link must name a route `App.tsx` really
 * registers (`.claude/rules/rules-frontend.md`) — two hand-typed copies is how
 * one of them goes stale silently.
 */
export const CUSTOM_FIELDS_ADMIN_PATH = '/admin/custom-fields';

export const relationshipClassKeys = {
  all: ['relationship-classes'] as const,
  list: (includeInactive: boolean) =>
    ['relationship-classes', { includeInactive }] as const,
  carrierCounts: ['relationship-classes', 'carrier-counts'] as const,
};

export function useRelationshipClasses(options?: { includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  return useQuery({
    queryKey: relationshipClassKeys.list(includeInactive),
    queryFn: async () => {
      const res = (await listRelationshipClassesApiV1MasterDataContactsRelationshipClassesGet({
        include_inactive: includeInactive,
      })) as { data: RelationshipClass[] };
      return res.data;
    },
  });
}

/**
 * How many {{organizations}} and {{contacts}} carry each class.
 *
 * A class is tenant-global while a custom-field definition belongs to exactly one
 * record type, so a field can be scoped to a class that no record of ITS type
 * carries — invisible everywhere, forever, with nothing saying so. The definition
 * dialog reads this to warn while the admin is still choosing.
 *
 * `enabled` because the route is module-admin gated (a bare record count would
 * otherwise sidestep the per-record visibility the list endpoints apply) and this
 * hook lives in a shared module: a non-admin surface mounting it would 403 on
 * every render. Callers pass `enabled: true` only from an admin surface.
 */
export function useRelationshipClassCarrierCounts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: relationshipClassKeys.carrierCounts,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      // No cast: the route carries a response_model, so orval types this call as
      // `RelationshipClassCarrierCountListResponse` already. The sibling hooks
      // above still cast because THEIR routes return an untyped envelope.
      const res =
        await relationshipClassCarrierCountsApiV1MasterDataContactsRelationshipClassesCarrierCountsGet();
      return res.data;
    },
  });
}

/**
 * Create a class, resolving to the CREATED row — not to the transport envelope.
 *
 * The route answers `{"data": {...}}` and orval's `customInstance` already
 * returns the response BODY, so the row is exactly one `.data` deep, the same
 * depth `useRelationshipClasses` above unwraps. Unwrapping it here rather than
 * at the call site is what makes that depth a single decision: the dialog used
 * to reach for `response.data.data`, which is `undefined` for every successful
 * create, so its `onCreated` callback never fired and a class created from the
 * contact/organization relationship picker was never attached to the record the
 * user created it from (WI 98b45988). Nothing could see it — the create really
 * did succeed, the class really did appear in the admin list, and the only
 * symptom was a picker that closed on an unchanged field.
 */
export function useCreateRelationshipClass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RelationshipClassCreateRequest) => {
      const res = (await createRelationshipClassApiV1MasterDataContactsRelationshipClassesPost(
        data
      )) as { data: RelationshipClass };
      return res.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: relationshipClassKeys.all }),
  });
}

export function useUpdateRelationshipClass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ classId, data }: { classId: string; data: RelationshipClassUpdateRequest }) =>
      updateRelationshipClassApiV1MasterDataContactsRelationshipClassesClassIdPut(classId, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: relationshipClassKeys.all }),
  });
}

export function useDeleteRelationshipClass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (classId: string) =>
      deleteRelationshipClassApiV1MasterDataContactsRelationshipClassesClassIdDelete(classId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: relationshipClassKeys.all }),
  });
}

/**
 * The classes a picker may offer: every ACTIVE class, plus any archived class
 * the record (or definition) already carries.
 *
 * An active-only option list is a one-way door. `is_active: false` means "can't
 * be applied to new records, but existing assignments are kept" — and a picker
 * that drops the archived class strands exactly those assignments: the value has
 * no option to label it, so `InlineEditableMultiToggle` renders the field EMPTY
 * while the class is still tagged on the record and still scoping custom fields
 * server-side, and the definition dialog silently re-saves a scope token it never
 * showed. Nothing errors, which is why both shipped. Callers pass the ids they
 * currently hold; order follows the server's `position`, so an archived class
 * stays where the admin left it rather than jumping to the end.
 *
 * See `.claude/rules/rules-frontend.md` § "A picker that can REMOVE must fetch
 * the retired options too". Fetch with `{ includeInactive: true }` — this filter
 * cannot re-add a class the request never returned.
 */
export function selectableRelationshipClasses(
  classes: RelationshipClass[] | undefined,
  selectedIds: readonly string[]
): RelationshipClass[] {
  const selected = new Set(selectedIds);
  return (classes ?? []).filter((c) => c.is_active || selected.has(c.id));
}

/** Resolve a class's display label for the active language (falls back to label). */
export function resolveRelationshipClassLabel(
  cls: RelationshipClass,
  lang: string
): string {
  const short = (lang || 'en').split('-')[0];
  return cls.label_translations?.[short] || cls.label_translations?.[lang] || cls.label;
}
